import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { Sex, SmokingStatus } from '@biosensesignal/web-sdk';
import { colors, gradients, shadows, fontSize, fontWeight, spacing, buttonDefaults } from '../style/tokens';
import { useTheme } from 'styled-components';

// ── Styled components ─────────────────────────────────────────────────────────

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 14px;
`;

const Field = styled.div<{ hasError?: boolean }>`
  background: #fff;
  border: 1.5px solid ${({ hasError, theme }) => (hasError ? '#d32f2f' : (theme.coloreBottoneInattivo || theme.gradient1))};
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FieldInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.theme.coloreBottoneInattivo || p.theme.gradient1};
  margin-bottom: ${spacing.sm};
`;

const FieldInput = styled.input`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  font-family: 'Outfit', sans-serif;
  color: ${colors.text};
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  padding: 0;
  &::placeholder { color: #ccc; font-weight: ${fontWeight.normal}; }
`;

const FieldError = styled.span`
  font-size: ${fontSize.xs};
  color: #d32f2f;
  margin-top: ${spacing.xs};
  display: block;
`;

// ── Feet + inches row ─────────────────────────────────────────────────────────

const FtInRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FtInInput = styled.input`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  font-family: 'Outfit', sans-serif;
  color: ${colors.text};
  border: none;
  outline: none;
  background: transparent;
  width: 36px;
  padding: 0;
  &::placeholder { color: #ccc; font-weight: ${fontWeight.normal}; }
`;

const FtInLabel = styled.span`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${colors.muted};
  margin-right: ${spacing.xs};
`;

// ── Unit toggle ───────────────────────────────────────────────────────────────

const UnitToggle = styled.div`
  display: flex;
  border: 1.5px solid ${p => p.theme.coloreBottoneInattivo || p.theme.gradient1};
  border-radius: 50px;
  overflow: hidden;
  flex-shrink: 0;
`;

const UnitBtn = styled.button<{ active: boolean }>`
  padding: 5px 11px;
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  font-family: 'Outfit', sans-serif;
  border: none;
  background: ${({ active, theme }) => (active ? (theme.coloreBottoneInattivo || buttonDefaults.inactive) : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : colors.muted)};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  line-height: 1;
`;

// ── Toggle group ──────────────────────────────────────────────────────────────

const ToggleGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const ToggleBtn = styled.button<{ active: boolean }>`
  padding: ${spacing.sm} ${spacing.xl};
  border-radius: 50px;
  border: 1.5px solid ${({ active, theme }) => (active ? (theme.coloreBottoneInattivo || buttonDefaults.inactive) : '#ddd')};
  background: ${({ active, theme }) => (active ? (theme.coloreBottoneInattivo || buttonDefaults.inactive) : '#fff')};
  color: ${({ active }) => (active ? '#fff' : colors.muted)};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  font-family: 'Outfit', sans-serif;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  @media (hover: hover) { &:hover { background: ${({ active, theme }) => (active ? (theme.coloreBottoneAttivo || buttonDefaults.active) : '#fff')}; border-color: ${({ active, theme }) => (active ? (theme.coloreBottoneAttivo || buttonDefaults.active) : '#ddd')}; } }
`;

// ── Action button ─────────────────────────────────────────────────────────────

const FormActions = styled.div`
  padding: 20px 14px 24px;
`;

const BtnNext = styled.button`
  display: block;
  width: 100%;
  padding: 18px 0;
  border-radius: 50px;
  border: none;
  background: ${p => p.theme.coloreBottoneInattivo || buttonDefaults.inactive};
  color: #fff;
  font-size: 17px;
  font-weight: ${fontWeight.bold};
  font-family: 'Outfit', sans-serif;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  @media (hover: hover) { &:hover { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; } }
  &:active { background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active}; transform: scale(0.98); }
`;


// ── Types ─────────────────────────────────────────────────────────────────────

export interface UserDemographics {
  sex: Sex;
  age: number;
  weight: number;
  height: number;
  smokingStatus: SmokingStatus;
}

interface DemographicsFormProps {
  isOpen: boolean;
  onSave: (demographics: UserDemographics) => void;
  onCancel: () => void;
  initialData?: UserDemographics;
}

interface FieldErrors {
  age?: string;
  weight?: string;
  height?: string;
}

type WeightUnit = 'kg' | 'lb';
type HeightUnit = 'cm' | 'ft';

const AGE_MIN = 18;
const AGE_MAX = 110;
const WEIGHT_MIN_KG = 40;
const WEIGHT_MAX_KG = 200;
const HEIGHT_MIN_CM = 130;
const HEIGHT_MAX_CM = 230;

function round1(n: number) { return Math.round(n * 10) / 10; }
function kgToLb(kg: number) { return round1(kg * 2.20462); }
function lbToKg(lb: number) { return round1(lb / 2.20462); }

function cmToFtIn(cm: number): { ft: number; inch: number } {
  const totalInches = cm / 2.54;
  const ft = Math.floor(totalInches / 12);
  const inch = Math.round(totalInches % 12);
  // handle edge case where rounding pushes inches to 12
  return inch === 12 ? { ft: ft + 1, inch: 0 } : { ft, inch };
}

// Floor variant for max-bound display: ensures the shown ft/in value actually
// round-trips to ≤ HEIGHT_MAX_CM (e.g. 230 cm → 7'6" not 7'7").
function cmToFtInFloor(cm: number): { ft: number; inch: number } {
  const totalInches = cm / 2.54;
  const ft = Math.floor(totalInches / 12);
  const inch = Math.floor(totalInches % 12);
  return { ft, inch };
}

function ftInToCm(ft: number, inch: number): number {
  return round1((ft * 12 + inch) * 2.54);
}

function weightBounds(unit: WeightUnit) {
  return unit === 'kg'
    ? { min: WEIGHT_MIN_KG, max: WEIGHT_MAX_KG }
    : { min: Math.ceil(kgToLb(WEIGHT_MIN_KG)), max: Math.floor(kgToLb(WEIGHT_MAX_KG)) };
}

// ── Component ─────────────────────────────────────────────────────────────────

const DemographicsForm = ({ isOpen, onSave, onCancel, initialData }: DemographicsFormProps) => {
  const intl = useIntl();

  // inside the component, before the useState calls:
  const { unitSystem } = useTheme();
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(unitSystem === 'imperial' ? 'lb' : 'kg');
  const [heightUnit, setHeightUnit] = useState<HeightUnit>(unitSystem === 'imperial' ? 'ft' : 'cm');

  const [sex, setSex] = useState<Sex>(initialData?.sex ?? Sex.MALE);
  const [age, setAge] = useState(initialData?.age?.toString() ?? '');

  // Weight: stored as kg in initialData, convert to display unit on init
  const [weight, setWeight] = useState(() => {
    if (!initialData?.weight) return '';
    return unitSystem === 'imperial'
      ? kgToLb(initialData.weight).toString()
      : initialData.weight.toString();
  });

  // Height: stored as cm in initialData, convert to display unit on init
  const [height, setHeight] = useState(() => {
    if (!initialData?.height || unitSystem === 'imperial') return '';
    return initialData.height.toString();
  });
  const [heightFt, setHeightFt] = useState(() => {
    if (!initialData?.height || unitSystem !== 'imperial') return '';
    return cmToFtIn(initialData.height).ft.toString();
  });
  const [heightInch, setHeightInch] = useState(() => {
    if (!initialData?.height || unitSystem !== 'imperial') return '';
    return cmToFtIn(initialData.height).inch.toString();
  });

  const [smokingStatus, setSmokingStatus] = useState<SmokingStatus>(
    initialData?.smokingStatus ?? SmokingStatus.NON_SMOKER,
  );
  const [errors, setErrors] = useState<FieldErrors>({});

  // Handles async initialData (e.g. token demographics arriving after first render).
  // weightUnit / heightUnit are intentionally omitted from deps — we only want to
  // re-populate when initialData itself changes, not when the user toggles the unit.
  useEffect(() => {
    if (!initialData) return;
    setSex(initialData.sex);
    setAge(initialData.age.toString());

    // weight: always kg in initialData → convert to current display unit
    setWeight(
      weightUnit === 'lb'
        ? kgToLb(initialData.weight).toString()
        : initialData.weight.toString(),
    );

    // height: always cm in initialData → convert to current display unit
    if (heightUnit === 'ft') {
      const { ft, inch } = cmToFtIn(initialData.height);
      setHeightFt(ft.toString());
      setHeightInch(inch.toString());
    } else {
      setHeight(initialData.height.toString());
    }

    setSmokingStatus(initialData.smokingStatus ?? SmokingStatus.NON_SMOKER);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWeightUnitChange = (unit: WeightUnit) => {
    if (unit === weightUnit) return;
    const val = parseFloat(weight);
    if (!isNaN(val)) {
      setWeight(unit === 'lb' ? kgToLb(val).toString() : lbToKg(val).toString());
    }
    setWeightUnit(unit);
    setErrors(p => ({ ...p, weight: undefined }));
  };

  const handleHeightUnitChange = (unit: HeightUnit) => {
    if (unit === heightUnit) return;
    if (unit === 'ft') {
      const cm = parseFloat(height);
      if (!isNaN(cm)) {
        const { ft, inch } = cmToFtIn(cm);
        setHeightFt(ft.toString());
        setHeightInch(inch.toString());
      }
    } else {
      const ft = parseInt(heightFt);
      const inch = parseInt(heightInch) || 0;
      if (!isNaN(ft)) {
        setHeight(ftInToCm(ft, inch).toString());
      }
    }
    setHeightUnit(unit);
    setErrors(p => ({ ...p, height: undefined }));
  };

  const validateFields = (): FieldErrors => {
    const newErrors: FieldErrors = {};
    const ageNum = parseInt(age);
    const weightNum = parseFloat(weight);
    const wb = weightBounds(weightUnit);

    if (!age || isNaN(ageNum)) {
      newErrors.age = intl.formatMessage({ id: 'validation.ageRequired' });
    } else if (ageNum < AGE_MIN) {
      newErrors.age = intl.formatMessage({ id: 'validation.ageMin' });
    } else if (ageNum > AGE_MAX) {
      newErrors.age = intl.formatMessage({ id: 'validation.ageMax' });
    }

    if (!weight || isNaN(weightNum)) {
      newErrors.weight = intl.formatMessage({ id: 'validation.weightRequired' });
    } else if (weightNum < wb.min) {
      newErrors.weight = `Min ${wb.min} ${weightUnit}`;
    } else if (weightNum > wb.max) {
      newErrors.weight = `Max ${wb.max} ${weightUnit}`;
    }

    if (heightUnit === 'cm') {
      const heightNum = parseFloat(height);
      if (!height || isNaN(heightNum)) {
        newErrors.height = intl.formatMessage({ id: 'validation.heightRequired' });
      } else if (heightNum < HEIGHT_MIN_CM || heightNum > HEIGHT_MAX_CM) {
        newErrors.height = `${HEIGHT_MIN_CM}–${HEIGHT_MAX_CM} cm`;
      }
    } else {
      const ft = parseInt(heightFt);
      const inch = parseInt(heightInch) || 0;
      if (!heightFt || isNaN(ft)) {
        newErrors.height = intl.formatMessage({ id: 'validation.heightRequired' });
      } else {
        // Math.round matches handleSubmit — avoids false errors at boundaries like 4'3" = 129.54 cm → 130 cm
        const totalCm = Math.round(ftInToCm(ft, inch));
        if (totalCm < HEIGHT_MIN_CM || totalCm > HEIGHT_MAX_CM) {
          const minFt = cmToFtIn(HEIGHT_MIN_CM);
          const maxFt = cmToFtInFloor(HEIGHT_MAX_CM);
          newErrors.height = `${minFt.ft}'${minFt.inch}"–${maxFt.ft}'${maxFt.inch}"`;
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = () => {
    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    const rawWeightKg = weightUnit === 'lb' ? lbToKg(parseFloat(weight)) : parseFloat(weight);
    const rawHeightCm = heightUnit === 'ft'
      ? ftInToCm(parseInt(heightFt), parseInt(heightInch) || 0)
      : parseFloat(height);
    onSave({
      sex,
      age: parseInt(age),
      weight: Math.round(rawWeightKg * 10) / 10,  // 1 d.p.
      height: Math.round(rawHeightCm),             // nearest integer cm
      smokingStatus,
    });
  };

  if (!isOpen) return null;

  const wb = weightBounds(weightUnit);

  return (
    <>
      <Fields>
        {/* Gender */}
        <Field>
          <FieldInner>
            <FieldLabel>{intl.formatMessage({ id: 'demographics.genderLabel' })}</FieldLabel>
            <ToggleGroup role="group" aria-label="Gender">
              <ToggleBtn active={sex === Sex.MALE} type="button" onClick={() => setSex(Sex.MALE)}>
                {intl.formatMessage({ id: 'form.male' })}
              </ToggleBtn>
              <ToggleBtn active={sex === Sex.FEMALE} type="button" onClick={() => setSex(Sex.FEMALE)}>
                {intl.formatMessage({ id: 'form.female' })}
              </ToggleBtn>
            </ToggleGroup>
          </FieldInner>
        </Field>

        {/* Age */}
        <Field hasError={!!errors.age} as="label">
          <FieldInner>
            <FieldLabel>{intl.formatMessage({ id: 'demographics.ageLabel' })}</FieldLabel>
            <FieldInput
              type="number"
              min={AGE_MIN}
              max={AGE_MAX}
              placeholder={intl.formatMessage({ id: 'demographics.agePlaceholder' })}
              inputMode="numeric"
              value={age}
              onChange={e => { setAge(e.target.value); if (errors.age) setErrors(p => ({ ...p, age: undefined })); }}
            />
            {errors.age && <FieldError>{errors.age}</FieldError>}
          </FieldInner>
          <span style={{ fontSize: 14, fontWeight: 600, color: colors.muted, flexShrink: 0 }}>
            {intl.formatMessage({ id: 'demographics.ageUnit' })}
          </span>
        </Field>

        {/* Weight */}
        <Field hasError={!!errors.weight}>
          <FieldInner>
            <FieldLabel>{intl.formatMessage({ id: 'demographics.weightLabel' })}</FieldLabel>
            <FieldInput
              type="number"
              min={wb.min}
              max={wb.max}
              placeholder={weightUnit === 'kg' ? intl.formatMessage({ id: 'demographics.weightPlaceholder' }) : 'e.g. 154'}
              inputMode="decimal"
              step={0.1}
              value={weight}
              onChange={e => { setWeight(e.target.value); if (errors.weight) setErrors(p => ({ ...p, weight: undefined })); }}
            />
            {errors.weight && <FieldError>{errors.weight}</FieldError>}
          </FieldInner>
          <UnitToggle>
            <UnitBtn active={weightUnit === 'kg'} type="button" onClick={() => handleWeightUnitChange('kg')}>kg</UnitBtn>
            <UnitBtn active={weightUnit === 'lb'} type="button" onClick={() => handleWeightUnitChange('lb')}>lb</UnitBtn>
          </UnitToggle>
        </Field>

        {/* Height */}
        <Field hasError={!!errors.height}>
          <FieldInner>
            <FieldLabel>{intl.formatMessage({ id: 'demographics.heightLabel' })}</FieldLabel>
            {heightUnit === 'cm' ? (
              <FieldInput
                type="number"
                min={HEIGHT_MIN_CM}
                max={HEIGHT_MAX_CM}
                placeholder={intl.formatMessage({ id: 'demographics.heightPlaceholder' })}
                inputMode="numeric"
                value={height}
                onChange={e => { setHeight(e.target.value); if (errors.height) setErrors(p => ({ ...p, height: undefined })); }}
              />
            ) : (
              <FtInRow>
                <FtInInput
                  type="number"
                  min={4}
                  max={7}
                  placeholder="5"
                  inputMode="numeric"
                  value={heightFt}
                  onChange={e => { setHeightFt(e.target.value); if (errors.height) setErrors(p => ({ ...p, height: undefined })); }}
                />
                <FtInLabel>ft</FtInLabel>
                <FtInInput
                  type="number"
                  min={0}
                  max={11}
                  placeholder="11"
                  inputMode="numeric"
                  value={heightInch}
                  onChange={e => { setHeightInch(e.target.value); if (errors.height) setErrors(p => ({ ...p, height: undefined })); }}
                />
                <FtInLabel>in</FtInLabel>
              </FtInRow>
            )}
            {errors.height && <FieldError>{errors.height}</FieldError>}
          </FieldInner>
          <UnitToggle>
            <UnitBtn active={heightUnit === 'cm'} type="button" onClick={() => handleHeightUnitChange('cm')}>cm</UnitBtn>
            <UnitBtn active={heightUnit === 'ft'} type="button" onClick={() => handleHeightUnitChange('ft')}>ft</UnitBtn>
          </UnitToggle>
        </Field>

        {/* Smoker */}
        <Field>
          <FieldInner>
            <FieldLabel>{intl.formatMessage({ id: 'demographics.smokerLabel' })}</FieldLabel>
            <ToggleGroup role="group" aria-label="Smoker">
              <ToggleBtn
                active={smokingStatus === SmokingStatus.SMOKER}
                type="button"
                onClick={() => setSmokingStatus(SmokingStatus.SMOKER)}
              >
                {intl.formatMessage({ id: 'demographics.yes' })}
              </ToggleBtn>
              <ToggleBtn
                active={smokingStatus === SmokingStatus.NON_SMOKER}
                type="button"
                onClick={() => setSmokingStatus(SmokingStatus.NON_SMOKER)}
              >
                {intl.formatMessage({ id: 'demographics.no' })}
              </ToggleBtn>
            </ToggleGroup>
          </FieldInner>
        </Field>
      </Fields>

      <FormActions>
        <BtnNext type="button" onClick={handleSubmit}>
          {intl.formatMessage({ id: 'form.continue' })}
        </BtnNext>
      </FormActions>

    </>
  );
};

export default DemographicsForm;
